<!--
  Empty-state dropzone, ported from vendor-ref/mara/app/ui/src/components/nanome/WorkspaceViewerDropzone.vue
  ('Click to Upload' / 'Or drag and drop files') with the RCSB id and URL inputs from WorkspaceImportFile.vue.
-->
<script setup lang="ts">
import { computed, ref } from 'vue';
import { useFileDialog } from '@vueuse/core';
import InputText from 'primevue/inputtext';
import Button from 'primevue/button';
import { ACCEPT_EXTS, SUPPORTED_EXTS, useLiteStore } from '@nanome-lite/state';

const store = useLiteStore();
const pdbId = ref('');
const url = ref('');
const loadingRcsb = ref(false);
const loadingUrl = ref(false);

const isEmpty = computed(() => store.entries.length === 0);
const loading = computed(() => store.isBusy);
const exts = SUPPORTED_EXTS.join(', ');

const { open, reset, onChange } = useFileDialog({ accept: ACCEPT_EXTS, multiple: true });
onChange(async (files) => {
  if (!files?.length) return;
  try { await store.addEntryFromFiles(Array.from(files)); }
  finally { reset(); }
});

async function importFromRcsb() {
  if (pdbId.value.length !== 4) return;
  loadingRcsb.value = true;
  try { await store.addEntryFromRcsb(pdbId.value); pdbId.value = ''; }
  catch { /* toasted */ }
  finally { loadingRcsb.value = false; }
}
async function importFromUrl() {
  if (!url.value.trim()) return;
  loadingUrl.value = true;
  try { await store.addEntryFromUrl(url.value); url.value = ''; }
  catch { /* toasted */ }
  finally { loadingUrl.value = false; }
}
</script>

<template>
  <div v-if="isEmpty" class="absolute inset-0 flex flex-col items-center justify-center gap-6 px-14 py-4 sm:px-4 text-center">
    <div class="flex flex-col items-center cursor-pointer" @click="loading || open()">
      <i :class="loading ? 'i-tabler-loader-2 animate-spin' : 'i-tabler-upload'" class="text-3xl mb-2" />
      <div class="text-sm">
        {{ loading ? `Loading ${store.loadingNames.join(', ') || '…'}` : 'Click to Upload' }}
      </div>
      <div class="text-xs op-50">
        Or drag and drop files
      </div>
      <div class="text-xs op-25 mt-1 max-w-80">
        {{ exts }} (optionally .gz)
      </div>
    </div>

    <div class="flex flex-col sm:flex-row gap-2 w-full max-w-xl">
      <form class="flex-1 flex gap-2 bg-xr rd-full p-1" @submit.prevent="importFromRcsb">
        <InputText v-model="pdbId" maxlength="4" class="flex-1 min-w-0 rd-full" placeholder="RCSB PDB ID, e.g. 1CRN" size="small" />
        <Button :disabled="pdbId.length !== 4 || loading" :loading="loadingRcsb" type="submit" label="Load" size="small" rounded />
      </form>
      <form class="flex-1 flex gap-2 bg-xr rd-full p-1" @submit.prevent="importFromUrl">
        <InputText v-model="url" class="flex-1 min-w-0 rd-full" placeholder="https://…/structure.cif" size="small" />
        <Button :disabled="!url.trim() || loading" :loading="loadingUrl" type="submit" label="Load" size="small" rounded />
      </form>
    </div>
  </div>
</template>
